import Button from 'components/Button';
import {
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation
} from 'legacy/events/hooks';
import { useMemo, useState, useEffect, useRef } from 'react';
import { RxDragHandleDots2 } from 'react-icons/rx';
import { IoChevronDown } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useMediaQuery } from 'theme/useMediaQuery';

import * as toast from 'legacy/common/utils/toast';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from '@hello-pangea/dnd';
import classNames from 'classnames';
import {
  OutgoingEventProductDto,
  OutgoingSectionDto
} from 'api/dtos/events/products';
import { TrashIcon, CheckmarkIcon } from './forms/TicketForm/components/icons';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  isDeleting
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-0 max-w-md w-full mx-4">
        <div className="flex items-center p-4 border-b">
          <div className="bg-[#F1E6E5] rounded-xl p-2 mr-2">
            <svg
              width="22"
              height="18"
              viewBox="0 0 22 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.72479 18C1.54146 18 1.37479 17.9542 1.22479 17.8625C1.07479 17.7708 0.958123 17.65 0.87479 17.5C0.791456 17.35 0.745623 17.1875 0.73729 17.0125C0.728956 16.8375 0.77479 16.6667 0.87479 16.5L10.1248 0.5C10.2248 0.333333 10.354 0.208333 10.5123 0.125C10.6706 0.0416667 10.8331 0 10.9998 0C11.1665 0 11.329 0.0416667 11.4873 0.125C11.6456 0.208333 11.7748 0.333333 11.8748 0.5L21.1248 16.5C21.2248 16.6667 21.2706 16.8375 21.2623 17.0125C21.254 17.1875 21.2081 17.35 21.1248 17.5C21.0415 17.65 20.9248 17.7708 20.7748 17.8625C20.6248 17.9542 20.4581 18 20.2748 18H1.72479ZM10.9998 15C11.2831 15 11.5206 14.9042 11.7123 14.7125C11.904 14.5208 11.9998 14.2833 11.9998 14C11.9998 13.7167 11.904 13.4792 11.7123 13.2875C11.5206 13.0958 11.2831 13 10.9998 13C10.7165 13 10.479 13.0958 10.2873 13.2875C10.0956 13.4792 9.99979 13.7167 9.99979 14C9.99979 14.2833 10.0956 14.5208 10.2873 14.7125C10.479 14.9042 10.7165 15 10.9998 15ZM10.9998 12C11.2831 12 11.5206 11.9042 11.7123 11.7125C11.904 11.5208 11.9998 11.2833 11.9998 11V8C11.9998 7.71667 11.904 7.47917 11.7123 7.2875C11.5206 7.09583 11.2831 7 10.9998 7C10.7165 7 10.479 7.09583 10.2873 7.2875C10.0956 7.47917 9.99979 7.71667 9.99979 8V11C9.99979 11.2833 10.0956 11.5208 10.2873 11.7125C10.479 11.9042 10.7165 12 10.9998 12Z"
                fill="#A4332A"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold">Delete This Category?</h3>
        </div>

        <div className="p-6">
          <div className="text-left mb-6 text-base font-normal text-[#575757]">
            <p>You&apos;re about to delete this category.</p>
            <p>Don&apos;t worry — your ticket tiers won&apos;t be lost.</p>
            <p>
              They&apos;ll just be uncategorized, and you can sort them anytime.
            </p>
          </div>

          <div className="flex justify-center space-x-4 w-full">
            <button
              type="button"
              onClick={onCancel}
              disabled={isDeleting}
              className="py-2 px-8 rounded-full bg-[#EFF2FF] text-[#3853C4] font-medium hover:bg-[#e0e5ff] transition-colors w-full"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="py-2 px-8 rounded-full bg-[#A4332A] text-white font-medium hover:bg-[#8a2b24] transition-colors w-full"
            >
              {isDeleting ? (
                <AiOutlineLoading3Quarters
                  size={20}
                  className="animate-spin mx-auto"
                />
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add a reusable button component for adding categories
interface AddCategoryButtonProps {
  eventId: string;
  className?: string;
}

const AddCategoryButton: React.FC<AddCategoryButtonProps> = ({
  eventId,
  className
}) => {
  const createSectionMutation = useCreateSectionMutation();

  return (
    <>
      {createSectionMutation.isLoading ? (
        <AiOutlineLoading3Quarters size={20} className="animate-spin" />
      ) : (
        <Button
          variant="secondary"
          className={className}
          onClick={() => {
            createSectionMutation.mutate({
              eventId,
              name: 'New Category'
            });
          }}
        >
          + Add a Category
        </Button>
      )}
    </>
  );
};

// Add this component for the "Add Tier" button
interface AddTierButtonProps {
  sectionId: string;
  onAddTier: (sectionId: string) => void;
}

const AddTierButton: React.FC<AddTierButtonProps> = ({
  sectionId,
  onAddTier
}) => (
  <div className="mb-3 px-2">
    <button
      type="button"
      className="mt-0 text-base text-[#3853C4] bg-white font-semibold"
      onClick={() => onAddTier(sectionId)}
    >
      + Add Public Tier to Category
    </button>
  </div>
);

interface SectionsProps {
  eventId: string;
  tiers?: OutgoingEventProductDto[];
  sections?: OutgoingSectionDto[];
  children: (
    tickets: OutgoingEventProductDto[],
    sectionId: string
  ) => React.ReactNode;
  onReorder?: (
    tiers: OutgoingEventProductDto[],
    sections: OutgoingSectionDto[]
  ) => void;
  onDeleteSection?: (sectionId: string) => void;
  addEventPricingTierToSection?: (
    sectionId: string,
    tier: OutgoingEventProductDto
  ) => void;
  setAddTierToSectionId?: (sectionId: string) => void;
}

const Sections = ({
  eventId,
  tiers,
  sections,
  children,
  onReorder,
  onDeleteSection,
  addEventPricingTierToSection,
  setAddTierToSectionId
}: SectionsProps) => {
  const createSectionMutation = useCreateSectionMutation();
  const updateSectionMutation = useUpdateSectionMutation();
  const deleteSectionMutation = useDeleteSectionMutation();
  const isMediumOrLarger = useMediaQuery('(min-width: 768px)');

  const [selectedSection, setSelectedSection] = useState<string>();
  const [sectionNames, setSectionNames] = useState<Record<string, string>>({});
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const inputRef = useRef<HTMLInputElement>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);

  // Focus input when section is selected
  useEffect(() => {
    if (selectedSection && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedSection]);

  // Watch for successful section creation
  useEffect(() => {
    if (createSectionMutation.isSuccess && sections?.length > 0) {
      // Get the latest section (highest sortIndex)
      const latestSection = sections.reduce((latest, current) =>
        current.sortIndex > latest.sortIndex ? current : latest
      );
      setSelectedSection(latestSection.id);
      handleNameChange(latestSection.id, latestSection.name);

      // Find the scrollable parent container
      setTimeout(() => {
        const scrollableParent = document.querySelector(
          '#modal-scroll-container'
        );
        if (scrollableParent) {
          scrollableParent.scrollTo({
            top: scrollableParent.scrollHeight
          });
        }

        createSectionMutation.reset();
      }, 100);
    }
  }, [createSectionMutation.isSuccess, sections]);

  const tiersBySection: Record<string, OutgoingEventProductDto[]> = useMemo(
    () =>
      tiers?.reduce((acc, tier) => {
        acc[tier.sectionId || 'default'] = [
          ...(acc[tier.sectionId || 'default'] || []),
          tier
        ];
        return acc;
      }, {}),
    [tiers]
  );

  const editSection = (sectionId: string, name: string) => {
    // Only update if it's a different section than the current one
    if (selectedSection !== sectionId) {
      setSelectedSection(sectionId);
      setSectionNames((prev) => ({ ...prev, [sectionId]: name }));
    }
  };

  const handleNameChange = (sectionId: string, name: string) => {
    setSectionNames((prev) => ({ ...prev, [sectionId]: name }));
  };

  const handleNameBlur = (sectionId: string): Promise<void> =>
    new Promise((resolve) => {
      updateSectionMutation.mutate(
        {
          eventId,
          sectionId,
          name: sectionNames[sectionId]
        },
        {
          onSuccess: () => {
            setSectionNames({});
            setSelectedSection(undefined); // Clear selected section on successful save
            resolve();
          },
          onError: (error) => {
            toast.showError({
              title: 'Error updating section',
              message: error.message
            });
            resolve();
          }
        }
      );
    });

  // Completely clear related state after successful deletion
  useEffect(() => {
    if (deleteSectionMutation.isSuccess) {
      setSelectedSection(undefined);
      deleteSectionMutation.reset();
    }
  }, [deleteSectionMutation.isSuccess]);

  const handleDeleteSection = (sectionId: string) => {
    const section = sections?.find((s) => s.id === sectionId);
    if (!section) {
      return;
    }

    // Blur the input field to hide the mobile keyboard
    if (inputRef.current) {
      inputRef.current.blur();
    }

    setSectionToDelete(sectionId);
    setDeleteModalOpen(true);
  };

  const confirmDeleteSection = () => {
    if (
      !sectionToDelete ||
      deleteSectionMutation.variables?.sectionId === sectionToDelete
    ) {
      return;
    }

    if (selectedSection === sectionToDelete) {
      setSelectedSection(undefined);
    }

    deleteSectionMutation.mutate(
      {
        eventId,
        sectionId: sectionToDelete
      },
      {
        onSuccess: () => {
          onDeleteSection?.(sectionToDelete);
          setDeleteModalOpen(false);
          setSectionToDelete(null);
        },
        onError: (error) => {
          toast.showError({
            title: 'Error deleting section',
            message: error.message
          });
        }
      }
    );
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    // Create base arrays for both sections and tiers
    const currentSections = Array.from(sections || []);
    const currentTiers = Array.from(tiers || []).sort(
      (a, b) => a.sortIndex - b.sortIndex
    );

    if (result.type === 'sections') {
      // Handle section reordering
      const [removedSection] = currentSections.splice(result.source.index, 1);
      currentSections.splice(result.destination.index, 0, removedSection);

      // Update sortIndex for all sections
      const updatedSections = currentSections.map((section, index) => ({
        ...section,
        sortIndex: index
      }));

      // Special handling for default section
      if (result.draggableId === 'default-section') {
        // Default section is being dragged, but we don't actually want to change its order
        // Just animate it back to its original position
        return;
      }

      onReorder?.(currentTiers, updatedSections);
      return;
    }

    if (result.type === 'tiers') {
      // Handle tier reordering
      const sourceSectionId = result.source.droppableId;
      const destinationSectionId = result.destination.droppableId;
      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      // Find the tier being moved
      const sourceSectionTiers = currentTiers.filter(
        (t) => (t.sectionId || 'default') === sourceSectionId
      );
      const tierToMove = sourceSectionTiers[sourceIndex];

      // Remove tier from source section
      const tierIndex = currentTiers.findIndex((t) => t.id === tierToMove.id);
      currentTiers.splice(tierIndex, 1);

      // Add tier to destination section
      const destinationSectionTiers = currentTiers.filter(
        (t) => (t.sectionId || 'default') === destinationSectionId
      );
      const insertAtIndex = Math.min(
        destinationIndex,
        destinationSectionTiers.length
      );

      // Find where to insert in the overall tiers array
      const insertBeforeTier = destinationSectionTiers[insertAtIndex];
      const insertPosition = insertBeforeTier
        ? currentTiers.findIndex((t) => t.id === insertBeforeTier.id)
        : currentTiers.length;

      // Insert the tier at the new position with updated sectionId
      const updatedTier = {
        ...tierToMove,
        sectionId: destinationSectionId
      };
      currentTiers.splice(insertPosition, 0, updatedTier);

      // Update sortIndex for all tiers
      const updatedTiers = currentTiers.map((tier, index) => ({
        ...tier,
        sortIndex: index
      }));

      onReorder?.(updatedTiers, currentSections);
    }
  };

  const sortedSections = useMemo(
    () => sections?.sort((a, b) => a.sortIndex - b.sortIndex),
    [sections]
  );

  return (
    <div className="">
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onConfirm={confirmDeleteSection}
        onCancel={() => {
          setDeleteModalOpen(false);
          setSectionToDelete(null);
          // Clear selected section to exit edit mode
          setSelectedSection(undefined);
        }}
        isDeleting={deleteSectionMutation.isLoading}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="sections">
          {(providedSections, snapshotSections) => (
            <div
              className={classNames('w-full')}
              ref={providedSections.innerRef}
              data-rfd-droppable-context-id={
                providedSections.droppableProps['data-rfd-droppable-context-id']
              }
              data-rfd-droppable-id={
                providedSections.droppableProps['data-rfd-droppable-id']
              }
            >
              <div
                className={classNames(
                  'grid grid-flow-row md:grid-flow-col-dense md:auto-cols-fr'
                )}
              >
                {/* // ! Default section for tiers without sectionId */}
                <div className="w-full order-first md:order-last">
                  <div
                    className={classNames(
                      'border-t-2 border-[#EBEBEB] -mx-4 md:mx-6 bg-white',
                      {
                        hidden: sortedSections?.length === 0,
                        block: sortedSections?.length > 0
                      }
                    )}
                  />
                  <div
                    className={classNames(
                      'md:px-6 pt-4 flex-col gap-3 bg-white rounded-lg',
                      {
                        hidden: sortedSections?.length === 0,
                        flex: sortedSections?.length > 0
                      }
                    )}
                  >
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleSection('default')}
                    >
                      <h3 className="text-lg font-semibold text-black">
                        Uncategorized
                        {tiersBySection?.default?.length > 0 && (
                          <span> ({tiersBySection?.default?.length})</span>
                        )}
                      </h3>
                      <IoChevronDown
                        size={24}
                        className={`ml-2 text-black transition-transform duration-200 ${
                          expandedSections.default ? '' : 'transform rotate-180'
                        }`}
                      />
                    </div>
                    <p className="text-sm text-[#575757] pb-2">
                      Tiers left here will appear in an
                      &apos;Uncategorized&apos; category at the end of the
                      category menu in the customer view.
                    </p>
                  </div>

                  <Droppable droppableId="default" type="tiers">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...provided.droppableProps}
                        className={classNames('mb-2 px-2 rounded-2xl')}
                      >
                        {!expandedSections.default &&
                          children(
                            (tiersBySection.default || []).sort(
                              (a, b) => a.sortIndex - b.sortIndex
                            ),
                            'default'
                          )}
                      </div>
                    )}
                  </Droppable>

                  <div className="pb-4" />
                  <div
                    className={classNames(
                      'border-b-2 border-[#EBEBEB] -mx-10 md:mx-2',
                      {
                        hidden: sortedSections?.length === 0,
                        'hidden md:block': sortedSections?.length > 0
                      }
                    )}
                  />
                </div>

                {/* // ! Other sections */}
                <div
                  className={classNames(
                    'w-full order-last md:order-first transition-colors duration-200 rounded-2xl border-2 border-dashed border-transparent ease-in',
                    {
                      hidden: sortedSections?.length === 0,
                      'bg-[#EFF2FF] !border-[#6585FF]':
                        snapshotSections.isDraggingOver
                    }
                  )}
                >
                  <div className="border-t-2 border-[#EBEBEB] -mx-4 md:mx-6" />
                  {sortedSections?.map((section, index) => (
                    <Draggable
                      index={index}
                      key={section.id}
                      draggableId={section.id}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...provided.draggableProps}
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...provided.dragHandleProps}
                          className={classNames(
                            'relative px-0 w-full border-2 border-transparent rounded-lg',
                            {
                              'w-full border-[#6585FF]': snapshot.isDragging
                            }
                          )}
                        >
                          <div key={section.id} className="w-full">
                            <div className="flex items-center">
                              <div
                                className={classNames(
                                  'pl-2 md:pl-5 w-full ml-auto flex flex-col bg-white rounded-2xl transition-[margin] p-0 duration-200 ease-in',
                                  {
                                    'shadow-[-3px_14px_19.3px_0px_rgba(32,32,32,0.1) border-2 border-[#6585FF]':
                                      snapshot.isDragging,
                                    '!mx-1':
                                      !snapshot.draggingOver &&
                                      snapshotSections.isDraggingOver
                                  }
                                )}
                              >
                                <div className="flex items-center">
                                  <div className="">
                                    <RxDragHandleDots2
                                      size={25}
                                      className="cursor-grab text-[#787878]"
                                    />
                                  </div>
                                  {selectedSection === section.id ? (
                                    <div className="flex items-center flex-1">
                                      <input
                                        ref={inputRef}
                                        placeholder="eg: New Category"
                                        value={
                                          sectionNames[section.id] ??
                                          section.name
                                        }
                                        onChange={(e) =>
                                          handleNameChange(
                                            section.id,
                                            e.target.value
                                          )
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleNameBlur(section.id);
                                          }
                                        }}
                                        className="text-xl font-medium text-gray-500 border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1"
                                      />

                                      <div className="flex items-center">
                                        {section.id !== 'default' && (
                                          <EditSectionButton
                                            section={section}
                                            onDelete={handleDeleteSection}
                                            deleteMutation={
                                              deleteSectionMutation
                                            }
                                            editSection={editSection}
                                            handleNameBlur={handleNameBlur}
                                            isSelected={
                                              selectedSection === section.id
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      className="flex items-center justify-between cursor-pointer py-6 pr-4 pl-2 flex-1"
                                      onClick={() => toggleSection(section.id)}
                                    >
                                      <p className="text-lg font-medium text-black text-left">
                                        {sectionNames[section.id] ??
                                          section.name}
                                        {tiersBySection?.[section.id]?.length >
                                          0 && (
                                          <span>
                                            {' '}
                                            (
                                            {
                                              tiersBySection?.[section.id]
                                                ?.length
                                            }
                                            )
                                          </span>
                                        )}
                                      </p>

                                      <div className="flex items-center">
                                        {section.id !== 'default' && (
                                          <EditSectionButton
                                            section={section}
                                            onDelete={handleDeleteSection}
                                            deleteMutation={
                                              deleteSectionMutation
                                            }
                                            editSection={editSection}
                                            handleNameBlur={handleNameBlur}
                                            isSelected={
                                              selectedSection === section.id
                                            }
                                          />
                                        )}
                                        <IoChevronDown
                                          size={24}
                                          className={`ml-2 text-black transition-transform duration-200 ${
                                            expandedSections[section.id]
                                              ? ''
                                              : 'transform rotate-180'
                                          }`}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div
                                  className={classNames('md:-ml-4 mr-4', {
                                    '-mt-4 md:-mt-6':
                                      !expandedSections[section.id]
                                  })}
                                >
                                  {!expandedSections[section.id] &&
                                    children(
                                      (tiersBySection[section.id] || []).sort(
                                        (a, b) => a.sortIndex - b.sortIndex
                                      ),
                                      section.id
                                    )}
                                  {addEventPricingTierToSection &&
                                    !expandedSections[section.id] && (
                                      <AddTierButton
                                        sectionId={section.id}
                                        onAddTier={(sectionId) => {
                                          setAddTierToSectionId?.(sectionId);
                                        }}
                                      />
                                    )}
                                </div>
                              </div>
                            </div>
                            <div
                              className={classNames(
                                'border-b-2 border-[#EBEBEB] -mx-10 md:mx-2',
                                {
                                  hidden:
                                    snapshot.isDragging ||
                                    snapshotSections.isDraggingOver
                                }
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {/* Desktop add category button - shown only on medium screens and up */}
                  {isMediumOrLarger && !snapshotSections.isDraggingOver && (
                    <div className="flex justify-center mt-4 pl-8">
                      <AddCategoryButton eventId={eventId} />
                    </div>
                  )}

                  {providedSections.placeholder}
                </div>
              </div>
            </div>
          )}
        </Droppable>

        {/* Mobile add category button - shown only on small screens */}
        {(!isMediumOrLarger ||
          (isMediumOrLarger && sortedSections?.length === 0)) && (
          <div className="flex flex-col items-center justify-center mb-24">
            <AddCategoryButton eventId={eventId} />
          </div>
        )}
      </DragDropContext>
    </div>
  );
};

interface EditSectionButtonProps {
  section: OutgoingSectionDto;
  onDelete: (sectionId: string) => void;
  deleteMutation: ReturnType<typeof useDeleteSectionMutation>;
  editSection: (sectionId: string, sectionName: string) => void;
  handleNameBlur: (sectionId: string) => Promise<void>;
  isSelected?: boolean;
}

const EditSectionButton = ({
  section,
  onDelete,
  deleteMutation,
  editSection,
  handleNameBlur,
  isSelected = false
}: EditSectionButtonProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    editSection(section.id, section.name || '');
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete(section.id);
  };

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsSaving(true);
    handleNameBlur(section.id).finally(() => {
      setIsSaving(false);
    });
  };

  if (isSelected) {
    return (
      <div className="flex items-center my-5 mx-4 gap-2 ">
        <button
          onClick={handleDeleteClick}
          type="button"
          className="group p-1 transition-colors"
          title="Delete section"
          aria-label={`Delete ${section.name} section`}
        >
          <div className="group-hover:[&_path]:fill-red-500">
            {deleteMutation?.variables?.sectionId === section.id ? (
              <AiOutlineLoading3Quarters size={20} className="animate-spin" />
            ) : (
              <TrashIcon />
            )}
          </div>
        </button>

        <button
          onClick={handleSaveClick}
          type="button"
          disabled={isSaving}
          className="group p-1 transition-colors"
          aria-label="Save section name"
        >
          {isSaving ? (
            <AiOutlineLoading3Quarters
              size={24}
              className="animate-spin text-black"
            />
          ) : (
            <div className="group-hover:[&_path]:fill-green-500">
              <CheckmarkIcon />
            </div>
          )}
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleEditClick}
      type="button"
      className="ml-4 mr-2 text-[#3853C4] text-base font-semibold"
      title="Edit section"
    >
      Edit
    </button>
  );
};

export default Sections;
